import { Component, Input, OnInit } from '@angular/core';
import * as Models from '../../../models/models-index';
import { HelpTextService } from '../../../services/help/helpText.service';
import { of } from 'rxjs';

@Component({
    selector: 'kpi-metric-detailed',
    templateUrl: './kpi-metric-detailed.component.html',
    styleUrls: ['../panel-elements.scss', './kpi-metric-detailed.scss'],
    standalone: false
})
export class KpiMetricDetailedComponent implements Models.ElementComponent, OnInit {
  @Input() set dataSets(value: Models.DataSet[]) {
    if (!!value && value.length > 0) {
      this.processData(value[0]);
      this.isDataSetLoaded$ = of(true);
    } else {
      this.isDataSetLoaded$ = of(false);
    }
  }

  isDataSetLoaded$ = of(false);

  @Input() settings: Models.ElementSettings;
  @Input() viewContext?: Models.ViewContext;

  metricValue: any;
  metricValuePreviousPeriod: string;
  metricValuePreviousYear: string;
  metricValuePOP: string;
  metricValueYOY: string;
  metricLabel: string;
  title: string;
  titleIconClasses: string;
  subtitleOne: string;
  subtitleTwo: string;
  subtitleTwoLabel: string;
  metricValuePOPValue: number;
  metricValueYOYValue: number;

  constructor(private helpTextService: HelpTextService) {}

  ngOnInit() {
  }

  private processData(dataSet: Models.DataSet): void {
    //determine if we should use the legacy approach with settings.metricName or the newer approach based on the metrics array in settings
    if (this.settings.metrics && this.settings.metrics.length > 0) {
      this.getValues(dataSet);
    } else {
      this.getValuesByLegacy(dataSet);
    }
  }

  getValues(dataSet: Models.DataSet) {
    const row = dataSet.rows[0];

    const metric = this.settings.metrics[0]; // Assuming you want the first metric
    const metricIndex = dataSet.columns.findIndex(col => col.name === metric.name);
    const metricPreviousPeriodIndex = dataSet.columns.findIndex(col => col.name === metric.previousPeriod);
    const metricPreviousYearIndex = dataSet.columns.findIndex(col => col.name === metric.previousYear);
    const metricIndexPOP = dataSet.columns.findIndex(col => col.name === metric.pop);
    const metricIndexYOY = dataSet.columns.findIndex(col => col.name === metric.yoy);

    this.metricValue = row[metricIndex].label ?? row[metricIndex].value;
    this.metricValuePreviousPeriod = row[metricPreviousPeriodIndex]?.label;
    this.metricValuePreviousYear = row[metricPreviousYearIndex]?.label;
    this.metricValuePOPValue = row[metricIndexPOP]?.value as number;
    this.metricValueYOYValue = row[metricIndexYOY]?.value as number;
    this.metricValuePOP = row[metricIndexPOP]?.label;
    this.metricValueYOY = row[metricIndexYOY]?.label;
    this.title = this.settings.title ?? dataSet.columns[metricIndex].displayName ?? dataSet.columns[metricIndex].name;
    this.titleIconClasses = this.settings.titleIconClasses;
    this.subtitleOne = this.metricValuePreviousPeriod;
  }

  getValuesByLegacy(dataSet: Models.DataSet) {
    const row = dataSet.rows[0];
    let metricIndex = dataSet.columns.findIndex(col => col.name === this.settings.metricName);

    if (metricIndex === -1) {
      console.error(`Metric ${this.settings.metricName} not found in the dataset.`);
      return;
    }

    let metricPreviousPeriodIndex = dataSet.columns.findIndex(col => col.name === this.settings.metricNamePreviousPeriod);
    let metricPreviousYearIndex = dataSet.columns.findIndex(col => col.name === this.settings.metricNamePreviousYear);
    let metricIndexPOP = dataSet.columns.findIndex(col => col.name === this.settings.metricNamePOP);
    let metricIndexYOY = dataSet.columns.findIndex(col => col.name === this.settings.metricNameYOY);

    this.metricValue = row[metricIndex]?.label ?? row[metricIndex]?.value?.toString() ?? 'N/A';
    this.metricValuePreviousPeriod = row[metricPreviousPeriodIndex]?.label ?? 'N/A';
    this.metricValuePreviousYear = row[metricPreviousYearIndex]?.label ?? 'N/A';
    this.metricValuePOPValue = row[metricIndexPOP]?.value as number;
    this.metricValueYOYValue = row[metricIndexYOY]?.value as number;
    this.metricValuePOP = row[metricIndexPOP]?.label ?? 'N/A';
    this.metricValueYOY = row[metricIndexYOY]?.label ?? 'N/A';
    this.title =
      this.settings.title ??
      dataSet.columns[metricIndex]?.displayName ??
      dataSet.columns[metricIndex]?.name ??
      '';
    this.titleIconClasses = this.settings.titleIconClasses;
    this.subtitleOne = this.metricValuePreviousPeriod;
  }

  getTrendArrowIconClass(trend: number): string {
    const isTrendUp = this.isTrendUp(trend);

    if (isTrendUp === null || trend === 0) {
      return 'fa fa-horizontal-rule';
    } else if (trend > 0) {
      return 'fa fa-chevron-circle-up chevron-icon';
    } else {
      return 'fa fa-chevron-circle-down chevron-icon';
    }
  }

  getTrendIconColor(trend: number): string {
    const isTrendUp = this.isTrendUp(trend);

    if (isTrendUp === null) {
      return 'bg-yellow';
    } else if (!isTrendUp) {
      return 'bg-red';
    } else {
      return 'bg-green';
    }
  }

  getTrendColor(trend: number): string {
    const isTrendUp = this.isTrendUp(trend);
    if (isTrendUp == null) {
      return 'text-yellow';
    } else if (isTrendUp) {
      return 'text-green';
    } else {
      return 'text-red';
    }
  }

  isTrendUp(value: number) {
    if (value === null || value === undefined || value === 0 || this.settings == null) return null;

    return value > 0 ? !this.settings.metricInverted : this.settings.metricInverted;
  }

  openHelpTextClicked(): void {
    if (this.settings) {
      this.helpTextService.openHelp(this.settings.helpTextKey, this.settings.helpTextTitle);
    }
  }
}
